export type ParsedDestinyRate = {
  fromCurrency: string;
  fromValue: number;
  toCurrency: string;
  toValue: number;
};

// {3,4}: la mayoria de codigos ISO son 3 letras, pero las stablecoins que
// maneja el backend (USDT, USDC) son 4 — sin esto, destinyRateString nunca
// se parsea para esos pares y getRateBreakdown cae siempre al fallback.
const DESTINY_RATE_REGEX =
  /^\s*(\d+(?:[.,]\d+)?)\s+([A-Z]{3,4})\s*=\s*([0-9][0-9.,]*)\s+([A-Z]{3,4})\s*$/;

function parseLocalizedNumber(raw: string): number {
  const hasDot = raw.includes('.');
  const hasComma = raw.includes(',');

  let normalized: string;

  if (hasDot && hasComma) {
    const lastDot = raw.lastIndexOf('.');
    const lastComma = raw.lastIndexOf(',');

    if (lastDot > lastComma) {
      normalized = raw.replace(/,/g, '');
    } else {
      normalized = raw.replace(/\./g, '').replace(',', '.');
    }
  } else if (hasComma) {
    normalized = raw.replace(',', '.');
  } else if (hasDot) {
    const dotCount = raw.split('.').length - 1;
    normalized = dotCount > 1 ? raw.replace(/\./g, '') : raw;
  } else {
    normalized = raw;
  }

  return Number(normalized);
}

/**
 * Parsea `destinyRateString` con el formato `<n> <CURR_ORIGEN> = <n> <CURR_DESTINO>`.
 * @param input - Cadena cruda del backend o valor nullable.
 * @returns Objeto tipado con el par y los valores, o `null` cuando la cadena es inválida.
 */
export function parseDestinyRateString(
  input: string | null | undefined
): ParsedDestinyRate | null {
  if (typeof input !== 'string' || input.trim() === '') {
    return null;
  }

  const match = input.match(DESTINY_RATE_REGEX);
  if (!match) {
    return null;
  }

  const [, fromValueRaw, fromCurrency, toValueRaw, toCurrency] = match;

  const fromValue = parseLocalizedNumber(fromValueRaw);
  const toValue = parseLocalizedNumber(toValueRaw);

  if (!Number.isFinite(fromValue) || !Number.isFinite(toValue)) {
    return null;
  }

  if (fromValue <= 0 || toValue <= 0) {
    return null;
  }

  return { fromCurrency, fromValue, toCurrency, toValue };
}
